/**
 * Validation Telemetry Service
 * 
 * Captures and logs validation failures with detailed context for debugging and user diagnostics.
 */

import { ErrorObject } from 'ajv';
import { blink } from './blink';

export interface ValidationFailure {
  timestamp: Date;
  schemaName: string;
  errorCount: number;
  errors: Array<{
    field: string;
    keyword: string;
    message: string;
    expected: any;
    actualType: string;
    actualValue: string;
  }>;
  context: {
    userId?: string;
    workflowId?: string;
    nodeId?: string;
    actionType?: string;
  };
  userDiagnostic: string;
  internalDiagnostic: string;
  severity: 'critical' | 'error' | 'warning';
}

export interface ValidationTelemetryReport {
  id: string;
  timestamp: Date;
  reportType: 'validation_failure';
  failures: ValidationFailure[];
  summary: {
    totalFailures: number;
    criticalCount: number;
    errorCount: number;
    warningCount: number;
    affectedSchemas: string[];
    affectedUsers: string[];
  };
}

class ValidationTelemetryService {
  private failures: ValidationFailure[] = [];
  private maxStoredFailures = 1000;

  /**
   * Log a validation failure with full context
   */
  logValidationFailure(
    schemaName: string,
    errors: ErrorObject[],
    data: unknown,
    context: ValidationFailure['context'] = {}
  ): ValidationFailure {
    const failure: ValidationFailure = {
      timestamp: new Date(),
      schemaName,
      errorCount: errors.length,
      errors: errors.map(err => ({
        field: err.instancePath || 'root',
        keyword: err.keyword,
        message: err.message || 'validation failed',
        expected: err.params,
        actualType: typeof err.data,
        actualValue: typeof err.data === 'object' 
          ? JSON.stringify(err.data).substring(0, 200) + '...'
          : String(err.data).substring(0, 200)
      })),
      context,
      userDiagnostic: this.generateUserDiagnostic(schemaName, errors),
      internalDiagnostic: this.generateInternalDiagnostic(schemaName, errors, data),
      severity: this.determineSeverity(schemaName, errors)
    };

    // Store failure for analysis
    this.failures.push(failure);
    
    // Trim if exceeds max
    if (this.failures.length > this.maxStoredFailures) {
      this.failures = this.failures.slice(-this.maxStoredFailures);
    }

    // Log to console with color coding
    this.logToConsole(failure);

    // Persist to database if critical
    if (failure.severity === 'critical') {
      this.persistToDatabase(failure).catch(err => 
        console.error('Failed to persist validation failure:', err)
      );
    }

    return failure;
  }

  /**
   * Generate user-friendly diagnostic message
   */
  private generateUserDiagnostic(schemaName: string, errors: ErrorObject[]): string {
    const errorMessages: string[] = [];

    for (const err of errors) {
      if (err.keyword === 'required') {
        errorMessages.push(
          `⚠️ Missing required field: "${err.params.missingProperty}"`
        );
      } else if (err.keyword === 'type') {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Invalid type for "${field}": expected ${err.params.type}, received ${typeof err.data}`
        );
      } else if (err.keyword === 'enum') {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Invalid value for "${field}": must be one of [${err.params.allowedValues?.join(', ')}]`
        );
      } else if (err.keyword === 'format') {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Invalid format for "${field}": expected ${err.params.format} format`
        );
      } else if (err.keyword === 'minLength') {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Value for "${field}" is too short: minimum ${err.params.limit} characters required`
        );
      } else if (err.keyword === 'minimum' || err.keyword === 'maximum') {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Value for "${field}" is out of range: ${err.message}`
        );
      } else {
        const field = err.instancePath.replace(/^\//, '').replace(/\//g, '.') || 'value';
        errorMessages.push(
          `⚠️ Validation error for "${field}": ${err.message}`
        );
      }
    }

    if (errorMessages.length === 0) {
      return `⚠️ Validation failed for ${schemaName}. Please check your input and try again.`;
    }

    return `Validation failed for ${schemaName}:\n\n${errorMessages.join('\n')}\n\nPlease correct these issues and try again.`;
  }

  /**
   * Generate detailed internal diagnostic for developers
   */
  private generateInternalDiagnostic(
    schemaName: string,
    errors: ErrorObject[],
    data: unknown
  ): string {
    const diagnosticLines = [
      `[VALIDATION FAILURE] Schema: ${schemaName}`,
      `Error Count: ${errors.length}`,
      `Timestamp: ${new Date().toISOString()}`,
      '',
      'Errors:',
      ...errors.map((err, index) => {
        const lines = [
          `  ${index + 1}. ${err.keyword} validation failed`,
          `     Path: ${err.instancePath || 'root'}`,
          `     Message: ${err.message}`,
          `     Params: ${JSON.stringify(err.params)}`,
          `     Data Type: ${typeof err.data}`,
          `     Data Value: ${JSON.stringify(err.data).substring(0, 200)}`
        ];
        
        if (err.keyword === 'required') {
          lines.push(`     Missing Field: ${err.params.missingProperty}`);
        }
        
        if (err.keyword === 'type') {
          lines.push(`     Expected: ${err.params.type}`);
          lines.push(`     Received: ${typeof err.data}`);
        }
        
        if (err.keyword === 'enum') {
          lines.push(`     Allowed Values: [${err.params.allowedValues?.join(', ')}]`);
        }
        
        return lines.join('\n');
      }),
      '',
      'Full Data Preview:',
      JSON.stringify(data, null, 2).substring(0, 1000) + (JSON.stringify(data).length > 1000 ? '...' : '')
    ];

    return diagnosticLines.join('\n');
  }

  /**
   * Determine severity based on error type and schema
   */
  private determineSeverity(
    schemaName: string,
    errors: ErrorObject[]
  ): 'critical' | 'error' | 'warning' {
    // Critical schemas that must always be valid
    const criticalSchemas = [
      'AgentResponse',
      'EmailMessage',
      'CalendarEvent',
      'OAuthTokenResponse',
      'WorkflowNode'
    ];

    if (criticalSchemas.includes(schemaName)) {
      return 'critical';
    }

    // Check if any required fields are missing
    const hasRequiredFieldError = errors.some(err => err.keyword === 'required');
    if (hasRequiredFieldError) {
      return 'error';
    }

    // Check if any type mismatches
    const hasTypeError = errors.some(err => err.keyword === 'type');
    if (hasTypeError) {
      return 'error';
    }

    return 'warning';
  }

  /**
   * Log to console with appropriate formatting
   */
  private logToConsole(failure: ValidationFailure) {
    const icon = failure.severity === 'critical' ? '🚨' : 
                 failure.severity === 'error' ? '❌' : '⚠️';
    
    console.group(`${icon} Validation Failure: ${failure.schemaName}`);
    console.log('Severity:', failure.severity.toUpperCase());
    console.log('Error Count:', failure.errorCount);
    console.log('Timestamp:', failure.timestamp.toISOString());
    
    if (failure.context.userId) {
      console.log('User ID:', failure.context.userId);
    }
    if (failure.context.workflowId) {
      console.log('Workflow ID:', failure.context.workflowId);
    }
    if (failure.context.nodeId) {
      console.log('Node ID:', failure.context.nodeId);
    }
    
    console.group('User Diagnostic:');
    console.log(failure.userDiagnostic);
    console.groupEnd();
    
    console.group('Internal Diagnostic:');
    console.log(failure.internalDiagnostic);
    console.groupEnd();
    
    console.groupEnd();
  }

  /**
   * Persist validation failure to database
   */
  private async persistToDatabase(failure: ValidationFailure): Promise<void> {
    try {
      await blink.db.telemetryReports.create({
        id: `vf_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        userId: failure.context.userId || null,
        reportType: 'validation_failure',
        metrics: JSON.stringify({
          schemaName: failure.schemaName,
          errorCount: failure.errorCount,
          errors: failure.errors,
          severity: failure.severity
        }),
        context: JSON.stringify(failure.context),
        timestamp: failure.timestamp.toISOString(),
        createdAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to save validation failure to database:', error);
    }
  }

  /**
   * Get recent failures
   */
  getRecentFailures(limit: number = 50): ValidationFailure[] {
    return this.failures.slice(-limit);
  }

  /**
   * Get failures for specific schema
   */
  getFailuresBySchema(schemaName: string, limit: number = 50): ValidationFailure[] {
    return this.failures
      .filter(f => f.schemaName === schemaName)
      .slice(-limit);
  }

  /**
   * Get failures for specific user
   */
  getFailuresByUser(userId: string, limit: number = 50): ValidationFailure[] {
    return this.failures
      .filter(f => f.context.userId === userId)
      .slice(-limit);
  }

  /**
   * Generate telemetry report
   */
  generateReport(timeRange?: { start: Date; end: Date }): ValidationTelemetryReport {
    let failures = this.failures;

    if (timeRange) {
      failures = failures.filter(f => 
        f.timestamp >= timeRange.start && f.timestamp <= timeRange.end
      );
    }

    const criticalCount = failures.filter(f => f.severity === 'critical').length;
    const errorCount = failures.filter(f => f.severity === 'error').length;
    const warningCount = failures.filter(f => f.severity === 'warning').length;

    const affectedSchemas = [...new Set(failures.map(f => f.schemaName))];
    const affectedUsers = [...new Set(
      failures
        .map(f => f.context.userId)
        .filter((uid): uid is string => uid !== undefined)
    )];

    return {
      id: `report_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      timestamp: new Date(),
      reportType: 'validation_failure',
      failures,
      summary: {
        totalFailures: failures.length,
        criticalCount,
        errorCount,
        warningCount,
        affectedSchemas,
        affectedUsers
      }
    };
  }

  /**
   * Clear stored failures (for testing or cleanup)
   */
  clear() {
    this.failures = [];
  }

  /**
   * Get statistics
   */
  getStats() {
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const lastHour = new Date(now.getTime() - 60 * 60 * 1000);

    const failures24h = this.failures.filter(f => f.timestamp >= last24h);
    const failuresHour = this.failures.filter(f => f.timestamp >= lastHour);

    const schemaDistribution: Record<string, number> = {};
    for (const failure of this.failures) {
      schemaDistribution[failure.schemaName] = 
        (schemaDistribution[failure.schemaName] || 0) + 1;
    }

    return {
      total: this.failures.length,
      last24h: failures24h.length,
      lastHour: failuresHour.length,
      bySchema: schemaDistribution,
      mostFailedSchema: Object.entries(schemaDistribution)
        .sort(([, a], [, b]) => b - a)[0]?.[0] || null
    };
  }
}

// Singleton instance
export const validationTelemetry = new ValidationTelemetryService();

export default validationTelemetry;
